import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Clock, Dumbbell, CheckSquare, Square, Check, CheckCircle2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Button, Spinner, useToast } from "@/components/earnos/kit";

export default function MoveDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [move, setMove] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState("");
  const actionLock = useRef(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const d = await apiFetch("/moves");
      const m = (d.moves || []).find(x => x.move_id === id);
      setMove(m || null); setNotes(m?.proof || "");
    } catch (e) { setLoadError(e.message || "Could not load this move."); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const startMission = async () => {
    if (actionLock.current || !move) return;
    actionLock.current = true; setSaving(true);
    try {
      const result = await apiFetch("/mission/missions", { method: "POST", body: {
        goal: `Complete this Money Move: ${move.title}. ${move.why || ""} Steps: ${(move.steps || []).join("; ")}. Produce usable work and verify the result. Request approval before external actions.`,
        money_move_id: id, source: "money_move",
      } });
      toast.show("Mission created. Follow progress and approvals in Missions.", "success");
      navigate("/mission?id=" + encodeURIComponent(result.mission_id));
    } catch (e) { toast.show(e.message || "Could not create mission", "error"); }
    finally { actionLock.current = false; setSaving(false); }
  };

  const complete = async () => {
    if (actionLock.current) return;
    actionLock.current = true;
    setSaving(true);
    try {
      await apiFetch(`/moves/${id}/complete`, { method: "POST", body: { proof: notes } });
      // RESULT → NEXT: let EARNOS recalculate the single best next action and route there.
      try {
        const r = await apiFetch("/next-action");
        const a = r.action;
        toast.show(`Done. Next: ${a.title}`, "success");
        navigate(a.where.route);
      } catch {
        toast.show("Money Move completed. Nice work!", "success");
        navigate(-1);
      }
    } catch (e) {
      toast.show(e?.message || "Failed", "error");
    } finally {
      actionLock.current = false; setSaving(false);
    }
  };

  const skip = async () => {
    if (actionLock.current) return;
    actionLock.current = true; setSaving(true);
    try {
      await apiFetch(`/moves/${id}/skip`, { method: "POST", body: { reason: skipReason } });
      setSkipOpen(false);
      toast.show("Skipped. Your plan can adapt anytime.", "info");
      navigate(-1);
    } catch (e) {
      toast.show(e?.message || "Failed", "error");
    } finally { actionLock.current = false; setSaving(false); }
  };

  const reopen = async () => {
    if (actionLock.current) return;
    actionLock.current = true; setSaving(true);
    try { await apiFetch(`/moves/${id}/reset`, { method: "POST" }); toast.show("Reopened", "info"); await load(); }
    catch (e) { toast.show(e.message || "Could not reopen move", "error"); }
    finally { actionLock.current = false; setSaving(false); }
  };

  if (loading) return <Spinner className="py-24" />;
  if (loadError) return <div role="alert" className="py-16 text-ink">{loadError} <button type="button" onClick={load} className="underline">Retry</button></div>;
  if (!move) return <div className="py-24 text-center text-ink">Move not found</div>;

  const isDone = move.status === "done";

  return (
    <div className="animate-fade-in max-w-2xl">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} aria-label="Back to your action plan" data-testid="move-back" className="text-ink"><ChevronLeft size={26} /></button>
        <span className="text-base font-bold text-ink">Day {move.day}</span>
      </div>

      <h1 className="text-2xl font-extrabold tracking-tight text-ink">{move.title}</h1>
      <div className="flex gap-2 mt-3">
        <span className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-surface-3 text-sm text-ink-2"><Clock size={14} />{move.estimated_time}</span>
        <span className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-surface-3 text-sm text-ink-2"><Dumbbell size={14} />{move.difficulty}</span>
      </div>

      <div className="rounded-xl bg-brand/10 border border-brand/30 p-4 mt-5">
        <p className="earnos-kicker">Why it matters</p>
        <p className="text-base text-ink mt-1 leading-relaxed">{move.why}</p>
      </div>

      <h3 className="text-lg font-bold text-ink mt-6">Step-by-step</h3>
      <div className="space-y-2 mt-2">
        {(move.steps || []).map((step, i) => (
          <button key={i} onClick={() => setChecked((c) => ({ ...c, [i]: !c[i] }))} className="w-full flex items-start gap-3 rounded-xl border border-line bg-surface-2 p-4 text-left">
            {checked[i] ? <CheckSquare size={22} className="text-ok shrink-0" /> : <Square size={22} className="text-ink-2 shrink-0" />}
            <span className={`text-base leading-relaxed ${checked[i] ? "text-ink-2 line-through" : "text-ink-3"}`}>{step}</span>
          </button>
        ))}
      </div>

      <Button title="Let EARNOS do this move" onClick={startMission} loading={saving} full className="mt-6" testID="move-mission" />
      <p className="text-sm text-ink-2 mt-2">EARNOS checks supported tools, requests needed connections and approvals, and verifies completed work.</p>
      <h3 className="text-lg font-bold text-ink mt-6 mb-2">Proof or notes (optional)</h3>
      <textarea aria-label="Proof or notes" maxLength={6000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add a link, note, or result..." rows={3} data-testid="move-notes" className="w-full rounded-xl border border-line bg-surface-2 p-4 text-base text-ink placeholder:text-ink-2 outline-none focus:border-brand-light" />

      <p className="text-sm text-ink-2 mt-3">Manual completion and notes are self-reported; they do not verify payment or earnings.</p>
      {!isDone ? (
        <>
          <Button title="Mark Complete" icon={Check} onClick={complete} loading={saving} full className="mt-6" testID="move-complete" />
          <button onClick={() => setSkipOpen(true)} data-testid="move-skip" className="w-full text-center text-base text-ink-2 py-4 mt-1 hover:text-ink">Skip this move</button>
        </>
      ) : (
        <div className="flex items-center gap-2 rounded-xl bg-ok/10 p-4 mt-6">
          <CheckCircle2 size={20} className="text-ok" />
          <span className="text-lg font-bold text-ok flex-1">Completed</span>
          <button disabled={saving} onClick={reopen} className="text-base font-bold text-brand-light">Reopen</button>
        </div>
      )}

      {skipOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setSkipOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-surface-2 border border-line p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-ink">Skip this move?</h3>
            <p className="text-base text-ink-2 mt-1 leading-relaxed">Your plan can adapt. No pressure — tell us why (optional).</p>
            <input value={skipReason} onChange={(e) => setSkipReason(e.target.value)} placeholder="Reason (optional)" data-testid="skip-reason" className="w-full mt-4 rounded-xl border border-line bg-surface p-3 text-base text-ink placeholder:text-ink-2 outline-none" />
            <div className="flex gap-3 mt-5">
              <Button title="Cancel" variant="secondary" onClick={() => setSkipOpen(false)} full />
              <Button title="Skip" onClick={skip} full testID="confirm-skip" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
